import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { resetPassword } from '../../services/authService';
import '../Login/Login.css';
import '../RegisterPage/RegisterPage.css';
import './ResetPasswordPage.css';

const CRITERION_CONTENT = {
  neutral: { icon: '\u25CB', text: 'Not checked' },
  valid: { icon: '\u2713', text: 'Requirement met' },
  invalid: { icon: '\u2715', text: 'Requirement not met' },
};

const PasswordCriterion = ({ id, state, children }) => {
  const content = CRITERION_CONTENT[state];

  return (
    <p
      className={'registerCriterion registerCriterion--' + state}
      id={id}
      aria-live="polite"
    >
      <span className="registerCriterionIcon" aria-hidden="true">
        {content.icon}
      </span>
      <span>{children}</span>
      <span className="registerScreenReaderText"> - {content.text}</span>
    </p>
  );
};

const RESULT_CONTENT = {
  success: {
    title: 'Password changed successfully',
    text: 'You can now sign in with your new password.',
  },
  invalid: {
    title: 'Password reset failed',
    text: 'Invalid or expired password reset link.',
  },
  error: {
    title: 'Password reset failed',
    text: 'Unable to reset the password. Please try again later.',
  },
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resultState, setResultState] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const passwordState = password
    ? password.length >= 12
      ? 'valid'
      : 'invalid'
    : 'neutral';
  const confirmPasswordState = confirmPassword
    ? password && password === confirmPassword
      ? 'valid'
      : 'invalid'
    : 'neutral';

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (!password) {
      setErrorMessage('Password is required.');
      return;
    }

    if (password.length < 12) {
      setErrorMessage('Password must be at least 12 characters.');
      return;
    }

    if (!confirmPassword) {
      setErrorMessage('Confirm password is required.');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setErrorMessage('');
    setIsSubmitting(true);

    try {
      await resetPassword(token, password);
      setResultState('success');
    } catch (error) {
      setResultState(error?.response?.status === 400 ? 'invalid' : 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <main className="loginPage">
        <section className="loginCard resetPasswordResult">
          <h1>Password reset link is invalid</h1>
          <p>The reset token is missing.</p>
          <button
            className="loginSubmitButton resetPasswordButton"
            type="button"
            onClick={() => navigate('/login')}
          >
            Go to Sign In
          </button>
        </section>
      </main>
    );
  }

  if (resultState) {
    const content = RESULT_CONTENT[resultState];

    return (
      <main className="loginPage">
        <section className="loginCard resetPasswordResult">
          <h1>{content.title}</h1>
          <p>{content.text}</p>
          <button
            className="loginSubmitButton resetPasswordButton"
            type="button"
            onClick={() => navigate('/login')}
          >
            Go to Sign In
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="loginPage">
      <section className="loginCard" aria-labelledby="reset-password-title">
        <h1 id="reset-password-title">Create a new password</h1>
        <form
          className="loginForm"
          onSubmit={handleSubmit}
          aria-busy={isSubmitting}
          noValidate
        >
          <div className="loginField">
            <label htmlFor="reset-password">New password</label>
            <div className="loginPasswordControl">
              <input
                id="reset-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-describedby="reset-password-criterion"
                disabled={isSubmitting}
              />
              <button
                className="loginPasswordToggle"
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                disabled={isSubmitting}
                onClick={() => setShowPassword((value) => !value)}
              >
                <span aria-hidden="true">{showPassword ? '\u25C9' : '\u25CB'}</span>
              </button>
            </div>
            <PasswordCriterion id="reset-password-criterion" state={passwordState}>
              At least 12 characters
            </PasswordCriterion>
          </div>

          <div className="loginField">
            <label htmlFor="reset-confirm-password">Confirm password</label>
            <div className="loginPasswordControl">
              <input
                id="reset-confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                aria-describedby="reset-confirm-password-criterion"
                disabled={isSubmitting}
              />
              <button
                className="loginPasswordToggle"
                type="button"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showConfirmPassword}
                disabled={isSubmitting}
                onClick={() => setShowConfirmPassword((value) => !value)}
              >
                <span aria-hidden="true">
                  {showConfirmPassword ? '\u25C9' : '\u25CB'}
                </span>
              </button>
            </div>
            <PasswordCriterion
              id="reset-confirm-password-criterion"
              state={confirmPasswordState}
            >
              Passwords match
            </PasswordCriterion>
          </div>

          {errorMessage && (
            <p className="loginError" role="alert">
              {errorMessage}
            </p>
          )}

          <button
            className="loginSubmitButton"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Resetting password...' : 'Reset password'}
          </button>
        </form>
      </section>
    </main>
  );
};

export default ResetPasswordPage;
